import React, { useState } from "react"
import ReactApexChart from "react-apexcharts"

import { POST, httpOk } from "helpers/api"
import { formatDate, formatMoney } from "helpers/helpers"

import KpiCard from "../parts/KpiCard"
import MetricHint from "../parts/MetricHint"
import {
	Card, EmptyNote, baseChartOptions, CHART_COLORS,
	convert, formatAmount, formatPercent, mutedStyle,
} from "../parts/common"

/** Выручка: MRR-мост, собираемость, дебиторка, доход по услугам. */
function Revenue({ data, currency, rates, onReload }) {
	const months = data.months || []
	const bridge = data.bridge || {}
	const services = data.services || []
	const debtors = data.debtors || []
	const unit = data.unit_economics || { months: [], keys: [] }

	const [draft, setDraft] = useState({})
	const [saving, setSaving] = useState(false)

	if (months.length === 0) {
		return <Card><EmptyNote>Нет начислений и платежей за период</EmptyNote></Card>
	}

	const lastMonth = months[months.length - 1]
	const amount = (value, month = lastMonth.month) =>
		formatAmount(convert(value, month, currency, rates), currency)
	const series = (rows, key) => rows.map((row) => Number(convert(row[key], row.month, currency, rates).toFixed(2)))

	const collectionChart = {
		options: {
			...baseChartOptions,
			chart: { ...baseChartOptions.chart, type: "line", height: 320 },
			colors: [CHART_COLORS[2], CHART_COLORS[1], CHART_COLORS[3]],
			stroke: { width: [0, 0, 3], curve: "smooth" },
			plotOptions: { bar: { borderRadius: 3, columnWidth: "55%" } },
			xaxis: { ...baseChartOptions.xaxis, categories: months.map((row) => row.label) },
			yaxis: [
				{ labels: { style: { colors: "#8791a1", fontSize: "11px" }, formatter: (value) => formatAmount(value, currency) } },
				{ show: false },
				{
					opposite: true, max: 120, min: 0,
					labels: { style: { colors: "#8791a1", fontSize: "11px" }, formatter: (value) => Math.round(value) + " %" },
				},
			],
		},
		series: [
			{ name: "Начислено", type: "column", data: series(months, "billed") },
			{ name: "Собрано", type: "column", data: series(months, "collected") },
			{ name: "Собираемость, %", type: "line", data: months.map((row) => row.collection_rate) },
		],
	}

	// Услуги приходят плоским списком (месяц × услуга) — разворачиваем в серии.
	const serviceNames = [...new Set(services.map((row) => row.service_name))]
	const serviceMonths = [...new Set(services.map((row) => row.month))]
	const servicesChart = {
		options: {
			...baseChartOptions,
			chart: { ...baseChartOptions.chart, type: "bar", height: 300, stacked: true },
			colors: CHART_COLORS,
			plotOptions: { bar: { borderRadius: 3, columnWidth: "55%" } },
			xaxis: { ...baseChartOptions.xaxis, categories: serviceMonths },
			yaxis: {
				labels: { style: { colors: "#8791a1", fontSize: "11px" }, formatter: (value) => formatAmount(value, currency) },
			},
			tooltip: { theme: "light", y: { formatter: (value) => formatAmount(value, currency) } },
		},
		series: serviceNames.map((name) => ({
			name,
			data: serviceMonths.map((month) => {
				const row = services.find((item) => item.month === month && item.service_name === name)
				return Number(convert(row?.amount || 0, month, currency, rates).toFixed(2))
			}),
		})),
	}

	const bridgeMonths = bridge.months || []
	const bridgeChart = {
		options: {
			...baseChartOptions,
			chart: { ...baseChartOptions.chart, type: "bar", height: 320, stacked: true },
			colors: [CHART_COLORS[0], CHART_COLORS[1], CHART_COLORS[2], CHART_COLORS[4]],
			plotOptions: { bar: { borderRadius: 3, columnWidth: "55%" } },
			xaxis: { ...baseChartOptions.xaxis, categories: bridgeMonths.map((row) => row.label) },
			yaxis: {
				labels: { style: { colors: "#8791a1", fontSize: "11px" }, formatter: (value) => formatAmount(value, currency) },
			},
			tooltip: { theme: "light", y: { formatter: (value) => formatAmount(value, currency) } },
		},
		series: [
			{ name: "Новые", data: series(bridgeMonths, "new_mrr") },
			{ name: "Рост тарифов", data: series(bridgeMonths, "expansion_mrr") },
			{ name: "Снижение", data: series(bridgeMonths, "contraction_mrr") },
			{ name: "Ушедшие", data: series(bridgeMonths, "churned_mrr") },
		],
	}

	const totalDebt = debtors.reduce((sum, row) => sum + Number(row.balance || 0), 0)

	// Расходов на маркетинг и продажи в системе нет — вводим руками. Без них
	// CAC, LTV/CAC и окупаемость привлечения считать не из чего.
	function draftValue(month, key) {
		const draftKey = `${month}:${key}`
		if (draft[draftKey] !== undefined) return draft[draftKey]

		const row = unit.months.find((item) => item.month === month)
		return row?.values?.[key] ?? ""
	}

	async function saveInput(month, key) {
		const draftKey = `${month}:${key}`
		if (draft[draftKey] === undefined) return

		setSaving(true)
		const response = await POST("/admin/analytics/manual-input", {
			period_month: `${month}-01`,
			input_key: key,
			amount: Number(draft[draftKey] || 0),
		}, { loader: false })
		setSaving(false)

		if (httpOk(response)) {
			setDraft((prev) => {
				const next = { ...prev }
				delete next[draftKey]
				return next
			})
			if (onReload) onReload()
		}
	}

	const lastUnit = unit.months.length > 0 ? unit.months[unit.months.length - 1] : null

	return (
		<>
			<div className="row g-2 mb-3">
				<div className="col-md-3">
					<KpiCard title="MRR" hintKey="metric_mrr_hint" value={amount(lastMonth.mrr)}
						sublabel={lastMonth.mrr_source === "snapshot" ? "по снимку тарифов" : "по начислениям"} />
				</div>
				<div className="col-md-3">
					<KpiCard title="Начислено" hintKey="metric_billed_hint" value={amount(lastMonth.billed)}
						sublabel={lastMonth.label} accent="#f0a04c" />
				</div>
				<div className="col-md-3">
					<KpiCard title="Собрано" hintKey="metric_collected_hint" value={amount(lastMonth.collected)}
						sublabel={`собираемость ${formatPercent(lastMonth.collection_rate)}`} accent="#1f9d57" />
				</div>
				<div className="col-md-3">
					<KpiCard title="Дебиторка" hintKey="metric_receivables_hint" value={amount(totalDebt)}
						sublabel={`должников: ${debtors.length}`} accent="#d23434" />
				</div>
			</div>

			<div className="row g-2 mb-2">
				<div className="col-12">
					<Card title="Начислено, собрано, собираемость"
						hint={<MetricHint hintKey="metric_collection_rate_hint" />}>
						<ReactApexChart options={collectionChart.options} series={collectionChart.series}
							type="line" height={320} />
					</Card>
				</div>
			</div>

			<div className="row g-2 mb-2">
				<div className="col-md-7">
					<Card title="MRR-мост" hint={<MetricHint hintKey="metric_mrr_bridge_hint" />}>
						{!bridge.available && <EmptyNote>{bridge.message}</EmptyNote>}
						{bridge.available && (
							<ReactApexChart options={bridgeChart.options} series={bridgeChart.series}
								type="bar" height={320} />
						)}
					</Card>
				</div>
				<div className="col-md-5">
					<Card title="Выручка по услугам">
						{services.length === 0 && <EmptyNote>Нет начислений</EmptyNote>}
						{services.length > 0 && (
							<ReactApexChart options={servicesChart.options} series={servicesChart.series}
								type="bar" height={300} />
						)}
					</Card>
				</div>
			</div>

			<div className="row g-2 mb-2">
				<div className="col-12">
					<Card title="Юнит-экономика: расходы на привлечение"
						hint={<MetricHint hintKey="metric_cac_hint" />}
						right={<span style={mutedStyle}>{saving ? "сохраняем…" : "вводится руками раз в месяц"}</span>}>

						{lastUnit && (
							<div className="row g-2 mb-3">
								<div className="col-md-4">
									<KpiCard title="CAC" hintKey="metric_cac_hint"
										value={lastUnit.cac ? amount(lastUnit.cac, lastUnit.month) : "—"}
										sublabel={`${lastUnit.label} · новых брендов: ${lastUnit.new_brands}`} accent="#f0a04c" />
								</div>
								<div className="col-md-4">
									<KpiCard title="Окупаемость привлечения" hintKey="metric_cac_payback_hint"
										value={lastUnit.payback_months ? `${lastUnit.payback_months} мес.` : "—"}
										sublabel="норма — меньше 18 месяцев" />
								</div>
								<div className="col-md-4">
									<KpiCard title="Расходы за месяц" value={amount(lastUnit.spend, lastUnit.month)}
										sublabel={lastUnit.label} accent="#9b5de5" />
								</div>
							</div>
						)}

						{unit.message && <div style={{ ...mutedStyle, marginBottom: "8px" }}>{unit.message}</div>}

						<div className="table-responsive">
							<table className="table table-sm table-bordered mb-0" style={{ fontSize: "12px" }}>
								<thead>
									<tr>
										<th className="text-center">Месяц</th>
										{unit.keys.map((item) => <th key={item.key} className="text-end">{item.label}</th>)}
										<th className="text-end">Новых брендов</th>
										<th className="text-end">CAC</th>
										<th className="text-end">Окупаемость</th>
									</tr>
								</thead>
								<tbody>
									{months.map((row) => {
										const unitRow = unit.months.find((item) => item.month === row.month)
										return (
											<tr key={row.month}>
												<td className="text-center">{row.label}</td>
												{unit.keys.map((item) => (
													<td key={item.key} className="text-end" style={{ width: "130px" }}>
														<input
															type="number"
															className="form-control form-control-sm text-end"
															style={{ fontSize: "12px" }}
															value={draftValue(row.month, item.key)}
															onChange={(event) => setDraft((prev) => ({
																...prev,
																[`${row.month}:${item.key}`]: event.target.value,
															}))}
															onBlur={() => saveInput(row.month, item.key)}
														/>
													</td>
												))}
												<td className="text-end">{unitRow ? unitRow.new_brands : "—"}</td>
												<td className="text-end">
													{unitRow?.cac ? formatMoney(Math.round(unitRow.cac)) : "—"}
												</td>
												<td className="text-end">
													{unitRow?.payback_months ? `${unitRow.payback_months} мес.` : "—"}
												</td>
											</tr>
										)
									})}
								</tbody>
							</table>
						</div>
					</Card>
				</div>
			</div>

			<Card title="Кто должен" right={<span style={mutedStyle}>отрицательный баланс активных и отключённых точек</span>}>
				{debtors.length === 0 && <EmptyNote>Должников нет</EmptyNote>}
				{debtors.length > 0 && (
					<div className="table-responsive" style={{ maxHeight: "420px", overflowY: "auto" }}>
						<table className="table table-sm table-hover table-bordered mb-0" style={{ fontSize: "12px" }}>
							<thead>
								<tr>
									<th>Точка</th>
									<th>Бренд</th>
									<th className="text-end">Долг</th>
									<th className="text-end">Тариф</th>
									<th className="text-center">Оплачено до</th>
								</tr>
							</thead>
							<tbody>
								{debtors.map((row) => (
									<tr key={row.pos_id}>
										<td>{row.name}</td>
										<td style={mutedStyle}>{row.login}</td>
										<td className="text-end" style={{ color: "var(--dark-fg-danger, #d23434)" }}>{amount(row.balance)}</td>
										<td className="text-end">{amount(row.tariff)}</td>
										<td className="text-center">{row.paid_until_at ? formatDate(row.paid_until_at, "dd.MM.yyyy") : "—"}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</Card>
		</>
	)
}

export default Revenue
